import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { guestCartClient, getOrCreateCartSessionId, CART_SESSION_KEY } from '@/integrations/supabase/cartClient';
import { useAuth } from '@/contexts/AuthContext';
import type { AlertType } from '@/hooks/useInlineAlert';

interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  delivery_mode?: 'printed' | 'digital' | null;
  selected_color?: string | null;
  registry_item_id?: string | null;
  registry_item?: {
    id: string;
    gift_registries: {
      id: string;
      title: string;
      user_id: string;
      shipping_address: {
        full_name?: string;
        phone?: string;
        address?: string;
        city?: string;
        province?: string;
        postal_code?: string;
      } | null;
    };
  } | null;
  product?: {
    id: string;
    title: string;
    price: number;
    images: string[];
    is_digital: boolean;
    seller_id: string;
    is_print_on_demand?: boolean;
    printful_variant_id?: number;
    shipping_cost?: number | null;
    pod_delivery_options?: string[];
    digital_price?: number | null;
    instruction_pdf_url?: string | null;
    stock_quantity?: number | null;
    is_customizable?: boolean | null;
    discount_price?: number | null;
    discount_start_date?: string | null;
    discount_end_date?: string | null;
  };
}

export interface CartAlert {
  type: AlertType;
  message: string;
}

export interface LastAddedInfo {
  productId: string;
  title: string;
  image?: string;
}

interface CartContextType {
  items: CartItem[];
  loading: boolean;
  addToCart: (productId: string, quantity?: number, registryItemId?: string, deliveryMode?: 'printed' | 'digital', selectedColor?: string) => Promise<void>;
  removeFromCart: (itemId: string) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  updateDeliveryMode: (itemId: string, deliveryMode: 'printed' | 'digital') => Promise<void>;
  clearCart: () => Promise<void>;
  getCartTotal: () => number;
  getItemCount: () => number;
  refetch: () => Promise<void>;
  lastAlert: CartAlert | null;
  clearAlert: () => void;
  lastAdded: LastAddedInfo | null;
  clearLastAdded: () => void;
}


const CartContext = createContext<CartContextType | undefined>(undefined);

const getOrCreateSessionId = getOrCreateCartSessionId;

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastAlert, setLastAlert] = useState<CartAlert | null>(null);
  
  // Use ref to avoid fetchCartItems in dependencies
  const userRef = useRef(user);
  userRef.current = user;

  const [lastAdded, setLastAdded] = useState<LastAddedInfo | null>(null);

  const clearAlert = useCallback(() => setLastAlert(null), []);
  const clearLastAdded = useCallback(() => setLastAdded(null), []);


  const fetchCartItems = useCallback(async () => {
    setLoading(true);
    try {
      const currentUser = userRef.current;
      // Simplified query without gift_registry JOIN for faster initial load
      // Full registry data is fetched only at checkout when needed
      const db = currentUser ? supabase : guestCartClient;
      let query = db
        .from('cart_items')
        .select(`
          id,
          product_id,
          quantity,
          delivery_mode,
          selected_color,
          registry_item_id,
          products (
            id,
            title,
            price,
            images,
            is_digital,
            seller_id,
            is_print_on_demand,
            printful_variant_id,
            shipping_cost,
            pod_delivery_options,
            digital_price,
            instruction_pdf_url,
            stock_quantity,
            is_customizable,
            discount_price,
            discount_start_date,
            discount_end_date
          )
        `);

      if (currentUser) {
        query = query.eq('user_id', currentUser.id);
      } else {
        const sessionId = getOrCreateSessionId();
        query = query.eq('session_id', sessionId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const cartItems: CartItem[] = (data || []).map((item: any) => ({
        id: item.id,
        product_id: item.product_id,
        quantity: item.quantity,
        delivery_mode: item.delivery_mode,
        selected_color: item.selected_color,
        registry_item_id: item.registry_item_id,
        registry_item: null, // Registry data loaded on-demand at checkout
        product: item.products,
      }));

      setItems(cartItems);
    } catch (error) {
      console.error('[CartContext] Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Use stable userId instead of user object to prevent re-renders
  const userId = user?.id;
  
  // Track if initial fetch has been done to prevent duplicates
  const hasFetchedRef = useRef(false);

  // Reset fetch flag when user changes
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [userId]);

  // Deferred initial fetch - use requestIdleCallback to wait for browser idle
  useEffect(() => {
    if (authLoading) return;
    if (hasFetchedRef.current) return;
    
    const doFetch = () => {
      hasFetchedRef.current = true;
      fetchCartItems();
    };
    
    // Use requestIdleCallback if available, fallback to setTimeout
    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(doFetch, { timeout: 2000 });
      return () => window.cancelIdleCallback(idleId);
    } else {
      const timer = setTimeout(doFetch, 1000);
      return () => clearTimeout(timer);
    }
  }, [authLoading, userId, fetchCartItems]);

  // Revalidate when user returns to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !authLoading) {
        fetchCartItems();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchCartItems, authLoading]);

  // Migrate cart items when user logs in
  useEffect(() => {
    const migrateCart = async () => {
      if (userId) {
        const sessionId = localStorage.getItem(CART_SESSION_KEY);
        if (sessionId) {
          const { data: sessionItems } = await guestCartClient
            .from('cart_items')
            .select('product_id, quantity')
            .eq('session_id', sessionId);

          if (sessionItems && sessionItems.length > 0) {
            for (const item of sessionItems) {
              const { data: existing } = await supabase
                .from('cart_items')
                .select('id, quantity')
                .eq('user_id', userId)
                .eq('product_id', item.product_id)
                .maybeSingle();

              if (existing) {
                await supabase
                  .from('cart_items')
                  .update({ quantity: existing.quantity + item.quantity })
                  .eq('id', existing.id);
              } else {
                await supabase
                  .from('cart_items')
                  .insert({
                    user_id: userId,
                    product_id: item.product_id,
                    quantity: item.quantity,
                  });
              }
            }

            await guestCartClient
              .from('cart_items')
              .delete()
              .eq('session_id', sessionId);

            fetchCartItems();
          }
        }
      }
    };

    if (!authLoading && userId) {
      migrateCart();
    }
  }, [userId, authLoading, fetchCartItems]);

  const addToCart = async (productId: string, quantity: number = 1, registryItemId?: string, deliveryMode?: 'printed' | 'digital', selectedColor?: string) => {
    try {
      const { data: productData } = await supabase
        .from('products')
        .select('is_sample, is_print_on_demand, pod_delivery_options, stock_quantity')
        .eq('id', productId)
        .single();

      if (productData?.is_sample) {
        setLastAlert({ type: 'error', message: 'Los productos de ejemplo no se pueden agregar al carrito' });
        return;
      }

      // Stock validation (0 = unlimited/made-to-order, only validate if > 0)
      if (productData?.stock_quantity !== null && productData?.stock_quantity !== undefined && productData.stock_quantity > 0) {
        const existingInCart = items
          .filter(i => i.product_id === productId)
          .reduce((sum, i) => sum + i.quantity, 0);
        if (existingInCart + quantity > productData.stock_quantity) {
          const available = productData.stock_quantity - existingInCart;
          setLastAlert({ 
            type: 'error', 
            message: available <= 0 
              ? `"${productData?.is_sample ? '' : ''}No hay más unidades disponibles de este producto` 
              : `Solo ${available} unidad(es) disponible(s)` 
          });
          return;
        }
      }

      // For POD products, determine delivery mode
      let finalDeliveryMode = deliveryMode;
      if (productData?.is_print_on_demand) {
        const options = productData.pod_delivery_options as string[] || ['printed'];
        if (!finalDeliveryMode) {
          // Default to first available option
          finalDeliveryMode = options.includes('printed') ? 'printed' : 'digital';
        }
      }

      // Para items de registry, no combinar con items existentes (cada uno puede tener diferente destino)
      // For POD items, also check delivery_mode to avoid combining different modes
      // Treat different colors as separate cart items
      const existingItem = !registryItemId && !productData?.is_print_on_demand && !selectedColor
        ? items.find((item) => item.product_id === productId && !item.registry_item_id && !item.selected_color)
        : !registryItemId && !productData?.is_print_on_demand && selectedColor
        ? items.find((item) => item.product_id === productId && item.selected_color === selectedColor && !item.registry_item_id)
        : productData?.is_print_on_demand && !registryItemId
        ? items.find((item) => item.product_id === productId && item.delivery_mode === finalDeliveryMode && !item.registry_item_id)
        : null;

      if (existingItem) {
        await updateQuantity(existingItem.id, existingItem.quantity + quantity);
        setLastAlert({ type: 'success', message: 'Cantidad actualizada' });
        setLastAdded({
          productId: existingItem.product_id,
          title: existingItem.product?.title || 'Producto',
          image: existingItem.product?.images?.[0],
        });
        return;
      }


      const insertData: any = {
        product_id: productId,
        quantity,
        registry_item_id: registryItemId || null,
        delivery_mode: finalDeliveryMode || null,
        selected_color: selectedColor || null,
      };

      // Verify the session is still valid on the server. It can silently expire
      // while the client-side `user` object persists, which would make the
      // insert fail the RLS check (auth.uid() IS NULL but user_id set).
      let activeUserId: string | null = null;
      if (user) {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          activeUserId = sessionData.session.user.id;
        } else {
          const { data: refreshed } = await supabase.auth.refreshSession();
          activeUserId = refreshed.session?.user.id ?? null;
        }
      }

      if (activeUserId) {
        insertData.user_id = activeUserId;
      } else {
        insertData.session_id = getOrCreateSessionId();
      }


      // Insert and get the new item with product data in one request
      const cartDb = activeUserId ? supabase : guestCartClient;
      const { data, error } = await cartDb
        .from('cart_items')
        .insert(insertData)
        .select(`
          id,
          product_id,
          quantity,
          delivery_mode,
          selected_color,
          registry_item_id,
          products (
            id,
            title,
            price,
            images,
            is_digital,
            seller_id,
            is_print_on_demand,
            printful_variant_id,
            shipping_cost,
            pod_delivery_options,
            digital_price,
            instruction_pdf_url,
            discount_price,
            discount_start_date,
            discount_end_date
          ),
          gift_registry_items (
            id,
            gift_registries (
              id,
              title,
              user_id,
              shipping_address
            )
          )
        `)
        .single();

      if (error) throw error;

      // Update local state directly - no refetch needed
      setItems(prev => [...prev, {
        id: data.id,
        product_id: data.product_id,
        quantity: data.quantity,
        delivery_mode: data.delivery_mode as 'printed' | 'digital' | null,
        selected_color: data.selected_color,
        registry_item_id: data.registry_item_id,
        registry_item: data.gift_registry_items as any,
        product: data.products as any,
      }]);

      const modeLabel = finalDeliveryMode === 'digital' ? ' (digital)' : '';
      const message = user && !activeUserId
        ? 'Producto agregado. Tu sesión expiró: iniciá sesión de nuevo para guardar tu carrito.'
        : registryItemId
        ? '🎁 Regalo agregado al carrito'
        : `Producto agregado al carrito${modeLabel}`;
      setLastAlert({ type: user && !activeUserId ? 'warning' : 'success', message });

      setLastAdded({
        productId: data.product_id,
        title: (data.products as any)?.title || 'Producto',
        image: (data.products as any)?.images?.[0],
      });

    } catch (error) {
      console.error('Error adding to cart:', error);
      setLastAlert({ type: 'error', message: 'Error al agregar al carrito' });
    }
  };

  const removeFromCart = async (itemId: string) => {
    try {
      const { error } = await (user ? supabase : guestCartClient)
        .from('cart_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      setItems((prev) => prev.filter((item) => item.id !== itemId));
      setLastAlert({ type: 'success', message: 'Producto eliminado del carrito' });
    } catch (error) {
      console.error('Error removing from cart:', error);
      setLastAlert({ type: 'error', message: 'Error al eliminar del carrito' });
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    if (quantity < 1) {
      await removeFromCart(itemId);
      return;
    }

    try {
      // Stock validation
      const item = items.find(i => i.id === itemId);
      if (item) {
        const { data: productData } = await supabase
          .from('products')
          .select('stock_quantity')
          .eq('id', item.product_id)
          .single();

        if (productData?.stock_quantity !== null && productData?.stock_quantity !== undefined && productData.stock_quantity > 0) {
          if (quantity > productData.stock_quantity) {
            setLastAlert({ type: 'error', message: `Solo hay ${productData.stock_quantity} unidad(es) disponible(s)` });
            return;
          }
        }
      }

      const { error } = await (user ? supabase : guestCartClient)
        .from('cart_items')
        .update({ quantity })
        .eq('id', itemId);

      if (error) throw error;

      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, quantity } : item
        )
      );
    } catch (error) {
      console.error('Error updating quantity:', error);
      setLastAlert({ type: 'error', message: 'Error al actualizar cantidad' });
    }
  };

  const updateDeliveryMode = async (itemId: string, deliveryMode: 'printed' | 'digital') => {
    try {
      const { error } = await (user ? supabase : guestCartClient)
        .from('cart_items')
        .update({ delivery_mode: deliveryMode })
        .eq('id', itemId);

      if (error) throw error;

      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, delivery_mode: deliveryMode } : item
        )
      );
      
      const modeLabel = deliveryMode === 'digital' ? 'digital' : 'impreso';
      setLastAlert({ type: 'success', message: `Cambiado a modo ${modeLabel}` });
    } catch (error) {
      console.error('Error updating delivery mode:', error);
      setLastAlert({ type: 'error', message: 'Error al cambiar modo de entrega' });
    }
  };

  const clearCart = async () => {
    try {
      let query = (user ? supabase : guestCartClient).from('cart_items').delete();

      if (user) {
        query = query.eq('user_id', user.id);
      } else {
        const sessionId = getOrCreateSessionId();
        query = query.eq('session_id', sessionId);
      }

      const { error } = await query;

      if (error) throw error;

      setItems([]);
    } catch (error) {
      console.error('Error clearing cart:', error);
      setLastAlert({ type: 'error', message: 'Error al vaciar el carrito' });
    }
  };

  const getCartTotal = useCallback(() => {
    const isDiscountActive = (p?: CartItem['product']) => {
      if (!p || p.discount_price == null || p.discount_price >= p.price) return false;
      const now = new Date();
      if (p.discount_start_date && new Date(p.discount_start_date) > now) return false;
      if (p.discount_end_date && new Date(p.discount_end_date) < now) return false;
      return true;
    };
    return items.reduce((total, item) => {
      const price = isDiscountActive(item.product) ? item.product!.discount_price! : (item.product?.price || 0);
      return total + price * item.quantity;
    }, 0);
  }, [items]);

  const getItemCount = useCallback(() => {
    return items.reduce((count, item) => count + item.quantity, 0);
  }, [items]);

  const contextValue = useMemo(() => ({
    items,
    loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateDeliveryMode,
    clearCart,
    getCartTotal,
    getItemCount,
    refetch: fetchCartItems,
    lastAlert,
    clearAlert,
    lastAdded,
    clearLastAdded,
  }), [items, loading, addToCart, removeFromCart, updateQuantity, updateDeliveryMode, clearCart, getCartTotal, getItemCount, fetchCartItems, lastAlert, clearAlert, lastAdded, clearLastAdded]);


  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
